<template>
  <section class="container mx-auto px-4 md:px-8 py-8">
    <!-- Breadcrumbs -->
    <nav aria-label="Breadcrumb" class="mb-4 text-sm text-gray-500">
      <ol class="flex flex-wrap items-center gap-1">
        <li>
          <NuxtLink to="/" class="hover:text-black transition"
            >Головна</NuxtLink
          >
          <span class="mx-1">/</span>
        </li>
        <li>
          <NuxtLink to="/catalog" class="hover:text-black transition"
            >Каталог</NuxtLink
          >
          <span class="mx-1">/</span>
        </li>
        <li class="text-black font-medium" aria-current="page">
          Шовкові наволочки
        </li>
      </ol>
    </nav>

    <h1 class="text-2xl md:text-3xl font-bold mb-2">
      Шовкові наволочки Mulberry'SS з натурального шовку
    </h1>
    <p class="text-gray-600 mb-6 max-w-2xl">
      Наволочки Mulberry'SS з 100% натурального шовку Mulberry 6A — для
      здорового волосся, гладкої шкіри та комфортного сну. Виготовлено в
      Україні.
    </p>

    <CatalogProductGrid
      :items="visibleFlat"
      :loading="pending && !visibleFlat.length"
      density="compact"
      empty-text="Поки немає товарів."
    />

    <!-- SEO-текст -->
    <article class="prose prose-pink max-w-3xl mx-auto mt-12 mb-8 seo-text">
      <h2>Чому шовкова наволочка Mulberry'SS — найкращий вибір для сну?</h2>
      <p>
        Натуральний шовк Mulberry 6A — це преміальний матеріал, який
        використовується для виробництва найякісніших шовкових наволочок у
        світі. На відміну від бавовни та сатину,
        <strong>шовкова наволочка</strong>
        має ідеально гладку поверхню, яка мінімізує тертя з волоссям та шкірою
        обличчя під час сну.
      </p>

      <h3>Переваги шовкових наволочок для волосся</h3>
      <p>
        Волосся не плутається, менше ламається і зберігає укладку значно довше.
        Якщо ви фарбуєте волосся або робите кератинове вирівнювання,
        <strong>наволочка з натурального шовку</strong> допоможе зберегти
        результат процедури. Шовк не вбирає вологу, тому волосся залишається
        зволоженим від вечірнього догляду до ранку.
      </p>

      <h3>Шовкова наволочка для шкіри обличчя</h3>
      <p>
        Дерматологи рекомендують спати на шовку людям зі схильністю до акне та
        зморшок. Гладка текстура не створює складок-заломів на шкірі, а
        гіпоалергенні властивості шовку роблять його ідеальним для чутливої
        шкіри. Крім того, шовк <strong>не вбирає нічний крем</strong>, тож
        засоби догляду працюють ефективніше.
      </p>

      <h3>Як обрати правильну шовкову наволочку?</h3>
      <p>
        Зверніть увагу на щільність тканини — оптимальним є показник від
        <strong>19 до 25 momme</strong>. Наші наволочки виготовлені з шовку
        щільністю 19 та 22 momme — це ідеальний баланс між м'якістю, міцністю та
        довговічністю. Кожна наволочка має прихований замок-блискавку, який не
        заважає під час сну.
      </p>

      <h3>Догляд за шовковою наволочкою</h3>
      <p>
        Прати шовкову наволочку рекомендуємо вручну або на делікатному режимі
        при температурі до 30°C. Використовуйте мішечок для прання у пральній
        машинці та спеціальний засіб для шовку або м'який шампунь. Не віджимати,
        сушити в розправленому вигляді. При правильному догляді
        <strong>шовкова наволочка Mulberry'SS</strong> прослужить вам роки.
      </p>
    </article>

    <!-- FAQ -->
    <section class="max-w-3xl mx-auto mb-12">
      <h2 class="text-xl font-bold mb-4">Часті запитання</h2>
      <div class="space-y-4">
        <details
          v-for="(faq, i) in faqs"
          :key="i"
          class="border rounded-lg p-4 group"
        >
          <summary
            class="cursor-pointer font-medium list-none flex items-center justify-between"
          >
            {{ faq.q }}
            <ChevronDown
              class="w-4 h-4 transition-transform group-open:rotate-180 shrink-0 ml-2"
            />
          </summary>
          <p class="mt-3 text-gray-700 text-sm leading-relaxed">
            {{ faq.a }}
          </p>
        </details>
      </div>
    </section>
  </section>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { useHead, useSeoMeta, useRuntimeConfig, useRequestURL } from "#imports";
import {
  expandLiteToItems,
  sortGridItems,
} from "~/composables/catalog/gridItems";
import { useTracking, buildGaListItems } from "~/composables/useTracking";
import { ChevronDown } from "lucide-vue-next";

definePageMeta({ ssr: true });

/* ---- Constants ---- */
const CATEGORY_SLUG = "pillowcases";
const TITLE =
  "Шовкові наволочки Mulberry'SS — купити наволочку з натурального шовку | mulberry'ss";
const DESCRIPTION =
  "Купити шовкову наволочку Mulberry'SS в Україні ✔ Натуральний шовк Mulberry 6A, 22 momme ✔ Для здорового волосся та шкіри обличчя ✔ Швидка доставка Новою Поштою";

const cfg = useRuntimeConfig();
const reqUrl = useRequestURL();
const siteUrl = String(
  cfg.public?.siteUrl || reqUrl.origin || "https://mulberryss.shop",
).replace(/\/$/, "");

/* ---- SSR data fetch ---- */
const { data: products, pending } = await useAsyncData(
  "catalog-pillowcases",
  () =>
    $fetch("/api/catalog/by-category", {
      query: { slug: CATEGORY_SLUG, max: 24 },
    }),
  { server: true },
);

/* ---- Розгортання variantsLite у картки (спільний код із каталогом) ---- */
// Синхронно і навмисно: сторінка SSR-на, асинхронне розгортання не встигло б
// до рендеру і товари не потрапили б у HTML.
const visibleFlat = computed(() =>
  sortGridItems(
    expandLiteToItems(
      (products.value || []).filter((p) => p.published !== false && !p.hidden),
    ),
    "new",
  ),
);

/* ---- GA4: показ списку ---- */
// Власний item_list_name: злиття з каталогом в одну сутність зробило б
// звіти гіршими, ніж їх відсутність.
const LIST_NAME = "catalog_pillowcases";
const LIST_ID = "/catalog/pillowcases";
const { ga: gaLog } = useTracking();

onMounted(() => {
  if (!visibleFlat.value.length) return;
  gaLog("view_item_list", {
    item_list_name: LIST_NAME,
    item_list_id: LIST_ID,
    items: buildGaListItems(visibleFlat.value, {
      name: LIST_NAME,
      id: LIST_ID,
    }),
  });
});

/* ---- FAQ data ---- */
const faqs = [
  {
    q: "Чому шовкова наволочка корисна для волосся?",
    a: "Шовкова наволочка значно зменшує тертя, завдяки чому волосся менше плутається, ламається і зберігає укладку. Особливо корисна для кучерявого, фарбованого чи пошкодженого волосся.",
  },
  {
    q: "Який розмір шовкової наволочки обрати?",
    a: "Обирайте розмір відповідно до вашої подушки. Наволочки Mulberry'SS доступні у стандартних розмірах 50×70 см та 70×70 см. Перевірте розмір перед замовленням на сторінці товару. За індивідуальним розміром наволочки - пишіть у наші соцмережі - пошиємо для Вас.",
  },
  {
    q: "Як прати шовкову наволочку?",
    a: "Рекомендуємо ручне прання або делікатний режим при 30°C з використанням мішечку для прання та м'якого засобу для шовку. Не використовуйте відбілювачі. Сушіть в розправленому вигляді, уникаючи прямих сонячних променів.",
  },
  {
    q: "Чим шовк Mulberry відрізняється від звичайного?",
    a: "Шовк Mulberry 6A — це найвищий ґатунок натурального шовку. Він виробляється шовкопрядами, які харчуються виключно листям шовковиці (mulberry tree). Це забезпечує найдовші, найміцніші та найгладкіші волокна.",
  },
  {
    q: "Скільки прослужить шовкова наволочка?",
    a: "При правильному догляді шовкова наволочка Mulberry'SS прослужить 4-6 років, зберігаючи свої корисні властивості. Це інвестиція у ваше здоров'я, красу та комфорт.",
  },
  {
    q: "Чи є доставка по Україні?",
    a: "Так, ми доставляємо Новою Поштою по всій Україні. Термін доставки — 2-5 робочих днів. Також доступна оплата при отриманні (накладений платіж).",
  },
];

/* ---- SEO head ---- */
useSeoMeta({
  title: TITLE,
  description: DESCRIPTION,
  ogTitle: TITLE,
  ogDescription: DESCRIPTION,
  ogType: "website",
  ogUrl: `${siteUrl}/catalog/pillowcases`,
  ogImage: `${siteUrl}/img/og-pillowcases.jpg`,
  twitterCard: "summary_large_image",
  twitterTitle: TITLE,
  twitterDescription: DESCRIPTION,
  twitterImage: `${siteUrl}/img/og-pillowcases.jpg`,
});

/* ---- JSON-LD: BreadcrumbList ---- */
const breadcrumbLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Головна",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Каталог",
      item: `${siteUrl}/catalog`,
    },
    {
      "@type": "ListItem",
      position: 3,
      name: "Шовкові наволочки",
      item: `${siteUrl}/catalog/pillowcases`,
    },
  ],
};

/* ---- JSON-LD: ItemList (top products for rich results) ---- */
const itemListLd = computed(() => {
  const items = (products.value || []).slice(0, 10).map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `${siteUrl}/product/${p.slug || p.id}`,
    name: p.title || "",
  }));
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Шовкові наволочки mulberry'ss",
    numberOfItems: items.length,
    itemListElement: items,
  };
});

/* ---- JSON-LD: FAQPage ---- */
const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a,
    },
  })),
};

useHead(() => ({
  title: TITLE,
  script: [
    {
      key: "ld-breadcrumb",
      type: "application/ld+json",
      children: JSON.stringify(breadcrumbLd),
    },
    {
      key: "ld-itemlist",
      type: "application/ld+json",
      children: JSON.stringify(itemListLd.value),
    },
    {
      key: "ld-faq",
      type: "application/ld+json",
      children: JSON.stringify(faqLd),
    },
  ],
}));
</script>

<style scoped>
.seo-text h2 {
  font-size: 1.25rem;
  font-weight: 700;
  margin-top: 2rem;
  margin-bottom: 0.75rem;
}
.seo-text h3 {
  font-size: 1.125rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}
.seo-text p {
  color: #374151;
  line-height: 1.75;
  margin-bottom: 1rem;
}
</style>
